import { EimDashboardCardsResponse } from '../../../__generated__/types';
import { IntlShape } from 'react-intl/src/types';
import { getI18n } from '../../../utils/Utils';

export type ExecutiveMetric = {
    label: string;
    value: string;
    context: string;
    tone: 'neutral' | 'positive' | 'warning' | 'critical';
    filterType?: string;
    hint?: React.ReactNode;
};

export type ExecutiveSection = {
    key: string;
    title: string;
    chip: string;
    description: string;
    metrics: ExecutiveMetric[];
};

export type ExecutiveThresholdConfig = {
    onTimePositiveThreshold: number;
    onTimeWarningThreshold: number;
    failureCriticalThreshold: number;
    overdueWarningThreshold: number;
    overdueCriticalThreshold: number;
    overdueHoursThreshold: number;
    coveragePositiveThreshold: number;
    coverageWarningThreshold: number;
    emailTransitSlaHours: number;
    portalTransitSlaHours: number;
    ediTransitSlaHours: number;
    enableValueZoom: boolean;
};

export const PROVIDER_BREAKDOWN_ACTION = 'PROVIDER_BREAKDOWN';

export type MonitoringDashboardCard = {
    title: string;
    value: number;
    type: string;
    icon: string;
    color: string;
};

export type CountryCodeSource = {
    country?: string | null;
    companyCode?: string | null;
};

type ExecutiveSectionsParams = {
    dashboardCounts?: EimDashboardCardsResponse;
    activePartnerTotal: number;
    activePartnerGroupCount: number;
    formatCount: (value?: number | null) => string;
    formatPercent: (value: number) => string;
    inboundShare: number;
    outboundShare: number;
    passRate: number;
    riskRate: number;
    intl: IntlShape;
    executiveMetrics?: any;
    thresholds: ExecutiveThresholdConfig;
};

export const getDashboardCardCountData = (dashboardCounts?: EimDashboardCardsResponse, intl?: IntlShape): MonitoringDashboardCard[] => [
    {
        title: intl ? getI18n('eim-card-total', 'Total', intl) : 'Total',
        value: dashboardCounts?.total ?? 0,
        type: 'total',
        icon: 'inbox',
        color: '#1d76d2',
    },
    {
        title: intl ? getI18n('eim-card-inbound', 'Inbound', intl) : 'Inbound',
        value: dashboardCounts?.inbound ?? 0,
        type: 'inbound',
        icon: 'download',
        color: '#10a07a',
    },
    {
        title: intl ? getI18n('eim-card-outbound', 'Outbound', intl) : 'Outbound',
        value: dashboardCounts?.outbound ?? 0,
        type: 'outbound',
        icon: 'upload',
        color: '#6b61ff',
    },
    {
        title: intl ? getI18n('eim-card-passed', 'Passed', intl) : 'Passed',
        value: dashboardCounts?.pass ?? 0,
        type: 'pass',
        icon: 'check-circle',
        color: '#1fa64a',
    },
    {
        title: intl ? getI18n('eim-card-failed', 'Failed', intl) : 'Failed',
        value: dashboardCounts?.fail ?? 0,
        type: 'fail',
        icon: 'close-circle',
        color: '#cf2e2e',
    },
    {
        title: intl ? getI18n('eim-card-in-transit', 'In Transit', intl) : 'In Transit',
        value: dashboardCounts?.transit ?? 0,
        type: 'transit',
        icon: 'swap',
        color: '#e58a00',
    },
    {
        title: intl ? getI18n('eim-card-overdue', 'Overdue', intl) : 'Overdue',
        value: dashboardCounts?.overdue ?? 0,
        type: 'overdue',
        icon: 'clock-circle',
        color: '#b55000',
    },
    {
        title: intl ? getI18n('eim-card-busn-transit', 'BUSN Transit', intl) : 'BUSN Transit',
        value: dashboardCounts?.transb ?? 0,
        type: 'transb',
        icon: 'branches',
        color: '#3a5f8f',
    },
];

const normalizeCountryCode = (value?: string | null): string => {
    const normalized = String(value ?? '').trim().toUpperCase();
    return normalized.length === 2 ? normalized : '';
};

const normalizeCompanyCode = (value?: string | null): string => String(value ?? '').trim().toUpperCase();

export const buildCompanyCountryLookup = <T extends CountryCodeSource>(items: T[]): Map<string, string> => {
    const lookup = new Map<string, string>();

    items.forEach((item) => {
        const companyCode = normalizeCompanyCode(item.companyCode);
        const countryCode = normalizeCountryCode(item.country);

        if (!companyCode || !countryCode) {
            return;
        }

        if (!lookup.has(companyCode)) {
            lookup.set(companyCode, countryCode);
        }
    });

    return lookup;
};

export const resolveCountryCode = <T extends CountryCodeSource>(item: T, companyCountryLookup?: Map<string, string>): string => {
    const directCountryCode = normalizeCountryCode(item.country);
    if (directCountryCode) {
        return directCountryCode;
    }

    const companyCode = normalizeCompanyCode(item.companyCode);
    if (!companyCode || !companyCountryLookup) {
        return '';
    }

    return companyCountryLookup.get(companyCode) ?? '';
};

export const getCountryDisplayName = (countryCode: string, regionNames: Intl.DisplayNames | null): string => {
    const normalizedCountryCode = normalizeCountryCode(countryCode);
    if (!normalizedCountryCode) {
        return '';
    }

    if (!regionNames) {
        return normalizedCountryCode;
    }

    try {
        return regionNames.of(normalizedCountryCode) ?? normalizedCountryCode;
    } catch {
        return normalizedCountryCode;
    }
};

export const getCountryFlagUrl = (countryCode?: string | null): string => {
    const normalizedCountryCode = normalizeCountryCode(countryCode);
    if (!normalizedCountryCode) {
        return '';
    }

    return `/images/flags/${normalizedCountryCode.toLowerCase()}.svg`;
};

export const escapeHtml = (value: string): string => value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

export const formatCountryCompanyLabel = <T extends CountryCodeSource>(
    item: T,
    companyCountryLookup?: Map<string, string>,
    regionNames?: Intl.DisplayNames | null
): string => {
    const countryCode = resolveCountryCode(item, companyCountryLookup);
    const countryName = getCountryDisplayName(countryCode, regionNames ?? null);
    if (countryName) {
        return countryName;
    }

    const companyCode = normalizeCompanyCode(item.companyCode);
    return companyCode || 'Unknown';
};

export const getExecutiveSections = ({
    dashboardCounts,
    activePartnerTotal,
    activePartnerGroupCount,
    formatCount,
    formatPercent,
    inboundShare,
    outboundShare,
    passRate,
    riskRate,
    intl,
    executiveMetrics,
    thresholds,
}: ExecutiveSectionsParams): ExecutiveSection[] => {
    const getOnTimeTone = (value: number): ExecutiveMetric['tone'] => {
        if (value >= thresholds.onTimePositiveThreshold) {
            return 'positive';
        }
        if (value >= thresholds.onTimeWarningThreshold) {
            return 'warning';
        }
        return 'critical';
    };

    const getCoverageTone = (value: number): ExecutiveMetric['tone'] => {
        if (value >= thresholds.coveragePositiveThreshold) {
            return 'positive';
        }
        if (value >= thresholds.coverageWarningThreshold) {
            return 'warning';
        }
        return 'critical';
    };

    return [
    {
        key: 'intake',
        title: getI18n('eim-intake-title', 'Intake', intl),
        chip: getI18n('eim-intake-chip', 'Volume story', intl),
        description: getI18n('eim-intake-description', 'Invoice load and mix entering EIM for the selected period.', intl),
        metrics: [
            {
                label: getI18n('eim-total-invoices', 'Total invoices', intl),
                value: formatCount(executiveMetrics?.volume?.totalProcessed ?? dashboardCounts?.total),
                context: typeof executiveMetrics?.volume?.volumeDelta === 'number'
                    ? `${intl.formatNumber(executiveMetrics.volume.volumeDelta, { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: 'always' as any })}% ${getI18n('eim-vs-previous-period', 'vs previous period', intl)}`
                    : getI18n('eim-overall-platform-volume', 'Overall platform volume', intl),
                tone: 'neutral',
                filterType: 'TOTAL',
            },
            {
                label: getI18n('eim-inbound-share', 'Inbound share', intl),
                value: formatPercent(inboundShare),
                context: `${formatCount(executiveMetrics?.volume?.inboundCount ?? dashboardCounts?.inbound)} ${getI18n('eim-inbound-invoices', 'inbound invoices', intl)}`,
                tone: 'positive',
                filterType: 'IN',
            },
            {
                label: getI18n('eim-outbound-share', 'Outbound share', intl),
                value: formatPercent(outboundShare),
                context: `${formatCount(executiveMetrics?.volume?.outboundCount ?? dashboardCounts?.outbound)} ${getI18n('eim-outbound-invoices', 'outbound invoices', intl)}`,
                tone: 'neutral',
                filterType: 'OUT',
            },
        ],
    },
    {
        key: 'processing',
        title: getI18n('eim-processing-title', 'Processing', intl),
        chip: getI18n('eim-processing-chip', 'Flow health', intl),
        description: getI18n('eim-processing-description', 'How smoothly invoices are moving through the operating flow.', intl),
        metrics: [
            {
                label: getI18n('eim-stp-rate', 'STP rate', intl),
                value: formatPercent(executiveMetrics?.flowHealth?.stpRate ?? passRate),
                context: getI18n('eim-straight-through-processing-rate', 'Straight-through processing rate', intl),
                tone: 'positive',
                filterType: PROVIDER_BREAKDOWN_ACTION,
                hint: getI18n('eim-v2-stp-rate-hint', 'STP rate = PASS invoices / total processed invoices × 100. Click "STP by channel" below to view channel metrics.', intl),
            },
            {
                label: getI18n('eim-on-time-rate', 'On-time rate', intl),
                value: formatPercent(executiveMetrics?.flowHealth?.onTimeRate ?? passRate),
                context: getI18n('eim-on-time-processing-rate', 'On-time processing performance', intl),
                tone: getOnTimeTone(executiveMetrics?.flowHealth?.onTimeRate ?? passRate),
                filterType: PROVIDER_BREAKDOWN_ACTION,
            },
            {
                label: getI18n('eim-transit-age', 'Transit age', intl),
                value: `${intl.formatNumber(executiveMetrics?.flowHealth?.avgTransitDays ?? 0, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}d`,
                context: executiveMetrics?.flowHealth?.transitDelta
                    ? `${getI18n('eim-transit-trend', 'Transit trend', intl)}: ${executiveMetrics.flowHealth.transitDelta}`
                    : getI18n('eim-items-still-moving', 'Items still moving through workflow', intl),
                tone: 'warning',
                filterType: 'INTRANSIT',
            },
        ],
    },
    {
        key: 'risk',
        title: getI18n('eim-exceptions-title', 'Exceptions', intl),
        chip: getI18n('eim-exceptions-chip', 'SLA attention', intl),
        description: getI18n('eim-exceptions-description', 'Exception volume that may need leadership visibility or intervention.', intl),
        metrics: [
            {
                label: getI18n('eim-failed-invoices', 'Failed invoices', intl),
                value: formatCount(executiveMetrics?.risk?.failedCount ?? dashboardCounts?.fail),
                context: getI18n('eim-invoices-in-failure-state', 'Invoices currently in failure state', intl),
                tone: 'critical',
                filterType: 'FAIL',
            },
            {
                label: getI18n('eim-overdue-backlog', 'Overdue backlog', intl),
                value: formatCount(executiveMetrics?.risk?.overdueCount ?? dashboardCounts?.overdue),
                context: getI18n('eim-invoices-beyond-timing', 'Invoices beyond expected timing', intl),
                tone: 'critical',
                filterType: 'OVERDUE',
            },
            {
                label: getI18n('eim-failure-rate', 'Failure rate', intl),
                value: formatPercent(executiveMetrics?.risk?.failureRate ?? riskRate),
                context: getI18n('eim-failed-plus-overdue', 'Failed plus overdue as share of total', intl),
                tone: (executiveMetrics?.risk?.failureRate ?? riskRate) > thresholds.failureCriticalThreshold ? 'critical' : 'warning',
            },
        ],
    },
    {
        key: 'coverage',
        title: getI18n('eim-coverage-title', 'Coverage', intl),
        chip: getI18n('eim-coverage-chip', 'Network reach', intl),
        description: getI18n('eim-coverage-description', 'Scale of active participation across the BMW partner network.', intl),
        metrics: [
            {
                label: getI18n('eim-active-partners', 'Active partners', intl),
                value: formatCount(executiveMetrics?.coverage?.activePartners ?? activePartnerTotal),
                context: getI18n('eim-partners-active-current-view', 'Partners active in the current network view', intl),
                tone: 'neutral',
                filterType: 'TOTAL',
            },
            {
                label: getI18n('eim-coverage-rate', 'Coverage rate', intl),
                value: formatPercent(executiveMetrics?.coverage?.coverageRate ?? 0),
                context: getI18n('eim-bmw-groups-represented', 'BMW groups represented in partner activity', intl),
                tone: getCoverageTone(executiveMetrics?.coverage?.coverageRate ?? 0),
            },
            {
                label: getI18n('eim-busn-transit', 'BUSN transit', intl),
                value: formatCount(dashboardCounts?.transb),
                context: executiveMetrics?.coverage?.topImpactedGroups?.[0]?.groupName
                    ? `${getI18n('eim-top-impacted-group-overdue', 'Top impacted group (overdue-based)', intl)}: ${executiveMetrics.coverage.topImpactedGroups[0].groupName}`
                    : getI18n('eim-business-transit-items', 'Business transit items requiring monitoring', intl),
                tone: 'warning',
                filterType: 'BUSN',
            },
        ],
    },
    ];
};